import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AuditionListSection from './components/AuditionListSection';
import ProTipsSection from './components/ProTipsSection';
import { useAuth } from '../../contexts/AuthContext';
import { Music, Lightbulb } from 'lucide-react';

const ToolsAdminPage = () => {
  const { user, isSuperUser } = useAuth();
  const [activeSection, setActiveSection] = useState('auditions');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Redirect non-superusers
  useEffect(() => {
    if (!isSuperUser()) {
      // This would be caught by ProtectedRoute, but just in case
      setError('You do not have permission to access this page.');
    }
  }, [isSuperUser]);

  const handleSectionChange = (section) => {
    setActiveSection(section);
    setError(null);
  };

  const handleRefresh = () => {
    setError(null);
    // Refresh will be handled by child components
    window.location?.reload();
  };

  if (!isSuperUser()) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-6xl">
          <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-12 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="AlertTriangle" size={32} className="text-red-600" />
            </div>
            <h3 className="text-xl font-heading font-semibold text-red-800 mb-3">
              Access Denied
            </h3>
            <p className="text-red-700 font-body mb-6">
              You need SuperUser permissions to access the Tools admin page.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Tools Admin - RoleCall</title>
        <meta name="description" content="Administrative tools for managing audition song lists and professional tips for performers." />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-6xl">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-heading font-bold text-white mb-2">
              Tools Admin
            </h1>
            <p className="text-blue-100 font-body">
              Manage audition song assignments and professional guidance tips for performers
            </p>
          </div>

          {/* Section Navigation */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex items-center space-x-2">
                <div className="flex bg-white bg-opacity-20 rounded-xl p-1">
                  <button
                    onClick={() => handleSectionChange('auditions')}
                    className={`
                      px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2
                      ${activeSection === 'auditions' ?'bg-white text-indigo-900 shadow-sm' :'text-white hover:bg-white hover:bg-opacity-10'
                      }
                    `}
                  >
                    <Icon name="Music" size={18} />
                    <span>Audition Lists</span>
                  </button>
                  <button
                    onClick={() => handleSectionChange('tips')}
                    className={`
                      px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2
                      ${activeSection === 'tips' ?'bg-white text-indigo-900 shadow-sm' :'text-white hover:bg-white hover:bg-opacity-10'
                      }
                    `}
                  >
                    <Icon name="Lightbulb" size={18} />
                    <span>Pro Tips</span>
                  </button>
                </div>
              </div>

              <Button
                variant="outline"
                onClick={handleRefresh}
                iconName="RefreshCw"
                iconPosition="left"
                disabled={isLoading}
                className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
              >
                Refresh
              </Button>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-6 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={20} className="text-red-800" />
                <p className="text-red-800 font-medium">{error}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                className="mt-4 border-red-400 text-red-600 hover:bg-red-400 hover:text-white rounded-lg"
                iconName="RefreshCw"
                iconPosition="left"
              >
                Try Again
              </Button>
            </div>
          )}

          {/* Main Content */}
          <div className="space-y-6">
            {activeSection === 'auditions' && (
              <AuditionListSection 
                user={user}
                onError={setError}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
              />
            )}
            
            {activeSection === 'tips' && (
              <ProTipsSection 
                user={user}
                onError={setError}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
              />
            )}
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-white border-opacity-20">
            <div className="flex items-center justify-between text-sm text-blue-100">
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={16} />
                <span className="font-caption">SuperUser Access Required</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Calendar" size={16} />
                <span className="font-caption">
                  Last updated: {new Date()?.toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </span>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default ToolsAdminPage;